<template>
  <div v-if="currentTestResult">
    <div class="tw-grid tw-grid-cols-2">
      <PrintTest
        :test-result-id="currentTestResult.id"
        :word-category-id="testResultInfo?.wordCategoryId"
        :word-list-id="testResultInfo?.wordListId"
        :score="printScore"
        :player-name="studentName"
        :test-date="testDate"
      />
      <div>
        <select
          v-if="testResults.length > 1"
          v-model="currentTestIndex"
          class="sf-form-control"
        >
          <option
            v-for="(tr, index) in testResults"
            :key="tr.id"
            :value="index"
          >
            {{ $filters.timeFormat(tr.createdon, "[dn] [d] [mn] [y] [h]:[i]", true) }}
          </option>
        </select>
      </div>
    </div>
    <div class="sm:tw-grid sm:tw-grid-cols-2">
      <div>
        Date of test: 
        <br>
        {{ $filters.timeFormat(currentTestResult.createdon, "[dn] [d] [mn] [y] [h]:[i]", true) }}
      </div>
      <div class="tw-text-right">Student: {{ studentName }}</div>
    </div>
    <div class="tw-text-center">
      <table class="sf-table tw-mx-auto">
        <thead>
          <tr>
            <th>Word</th>
            <th>Attempt 1</th>
            <th>Attempt 2</th>
            <th>Attempt 3</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="w in testWords" :key="w.word.id">
            <td>
              {{ w.word.value }}
            </td>
            <td v-for="(att, i) in w.attempts" :key="i">
              <div v-if="att.is_correct">CORRECT</div>
              <div v-else class="tw-text-danger">{{ att.input }} (X)</div>
            </td>
            <td v-for="i in 3 - w.attempts.length" :key="i"></td>
          </tr>
        </tbody>
      </table>
    </div>
    <div>
      <div class="tw-font-bold">Results</div>
      <div class="tw-grid tw-grid-cols-4">
        <div class="tw-text-center">
          <div>1st Attempt</div>
          <div>
            {{ currentTestResult.scoreDetails?.a1 }} /
            {{ currentTestResult.scoreDetails?.tot }}
          </div>
        </div>
        <div class="tw-text-center">
          <div>2nd Attempt</div>
          <div>
            {{ currentTestResult.scoreDetails?.a2 }} /
            {{ currentTestResult.scoreDetails?.tot }}
          </div>
        </div>
        <div class="tw-text-center">
          <div>3rd Attempt</div>
          <div>
            {{ currentTestResult.scoreDetails?.a3 }} /
            {{ currentTestResult.scoreDetails?.tot }}
          </div>
        </div>
        <div class="tw-text-center">
          <div>Incorrect 3 times</div>
          <div>
            {{ currentTestResult.scoreDetails?.wrong }} /
            {{ currentTestResult.scoreDetails?.tot }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { SfTestResultInfo, SfUser } from "@/types/generalTypes";
import { Dict } from "@/types/generalTypes";
import apiCall from "@/func/net/apiCall";
import {
  SfSpellingTestResult,
  SfSpellingTestResultWord,
} from "@/types/spellingTypes";
import PrintTest from "./PrintTest.vue";

export default defineComponent({
  components: { PrintTest },
  props: {
    student: { type: Object as PropType<SfUser | null> },
    guestName: { type: String, default: "" },
    testResultInfo: { type: Object as PropType<SfTestResultInfo> },
    resultData: { type: Object as PropType<SfSpellingTestResult | null> },
  },
  data() {
    return {
      currentTestIndex: 0,
      testResults: [] as SfSpellingTestResult[],
    };
  },
  computed: {
    apiParams(): Dict<any> {
      if (!this.student) {
        return {};
      }
      const shared = {
        all: 1,
        student_id: this.student.id,
        expand: "resultData,scoreDetails,diplayName",
        mode: "test",
      };
      return { ...shared, ...this.testResultInfo };
    },
    currentTestResult(): SfSpellingTestResult {
      return this.testResults[this.currentTestIndex];
    },
    testDate(): Date {
      return new Date(this.currentTestResult.createdon.replaceAll("-", "/") + ' UTC');
    },
    testWords(): SfSpellingTestResultWord[] {
      return this.currentTestResult.resultData
        ? this.currentTestResult.resultData
        : [];
    },
    studentName(): string {
      return this.student ? this.student.fullName : this.guestName;
    },
    printScore(): Dict<any> | null {
      if (this.testResultInfo?.testResultId) {
        return null;
      }
      return this.testWords;
    },
  },
  mounted() {
    this.refreshResults();
  },
  methods: {
    async refreshResults() {
      if (this.resultData) {
        this.testResults = [this.resultData];
        return;
      }
      this.currentTestIndex = 0;
      const resData = await apiCall("test-result", this.apiParams);
      if (resData.success) {
        this.testResults = resData.data.items;
        this.$emit("dataloaded");
      }
    },
  },
});
</script>
